import './styles.scss';

import { ReactElement, useEffect, useMemo, useState } from 'react';
import { createNotification, handleError } from '@/utils';

import Form from '@/common/Form';
import Modal from '@/common/Modal';
import ProgressBar from '@/common/ProgressBar';
import Step1 from '@/pages/Users/components/CompleteEvaluationWizard/Step1';
import Step2 from '@/pages/Users/components/CompleteEvaluationWizard/Step2';
import { Toast } from '@/models';
import { CompleteEvaluationWizardTypes as Types } from '@/pages/Users/components/CompleteEvaluationWizard/types';
import { submitEvaluation } from '@/pages/Users/api/services/submitEvaluation';
import useSession from '@/state/session';
import useTrainingChecklistTableState from '@/pages/Users/components/TrainingChecklistTable/hook';

type CompleteEvaluationWizardProps = {
  show: boolean;
  userId: string;
  userFullName: string;
  userEmployeeId: string;
  userRoleName: string;
  onHide: () => void;
};

const initFormValues: Types.FormValues = {
  overallResult: '',
  fieldNotes: '',
};

const CompleteEvaluationWizard = ({
  show,
  userId,
  userFullName,
  userEmployeeId,
  userRoleName,
  onHide,
}: CompleteEvaluationWizardProps): ReactElement => {
  const [step, setStep] = useState<Types.Step>(Types.Step.FIELD_NOTES);
  const trainerFullName = useSession(({ state }) => `${state.user?.firstName || ''} ${state.user?.lastName || ''}`.trim());

  const { evaluationId, categoryWithQuestionMap, load } = useTrainingChecklistTableState(({ state }) => ({
    evaluationId: state.evaluationId,
    categoryWithQuestionMap: state.categoryWithQuestionMap,
    load: state.load,
  }));

  // Reset to Step 1 every time the wizard is (re)opened.
  useEffect((): void => {
    if (show) setStep(Types.Step.FIELD_NOTES);
  }, [show]);

  const startDate = useMemo((): string | null => {
    const submittedDates = Object.values(categoryWithQuestionMap || {})
      .map(({ category }) => category.submittedAt)
      .filter(Boolean)
      .sort();
    return submittedDates[0] || null;
  }, [categoryWithQuestionMap]);

  const handleCancel = (): void => onHide();

  const handleNext = async (values: Types.FormValues): Promise<void> => {
    setStep(Types.Step.COMPLETION);
  };

  const handleSubmit = async (values: Types.FormValues): Promise<void> => {
    try {
      if (!values.overallResult) throw new Error('submitEvaluation: no overall result provided');
      await submitEvaluation(evaluationId, values.overallResult, values.fieldNotes);
      // Re-check eligibility from scratch, same as if the modal were opened fresh, so a
      // just-submitted (now closed) attempt doesn't leave the checklist looking completable.
      await load(userId);
      onHide();
      createNotification(`Evaluation for ${userFullName} has been submitted.`, Toast.Type.SUCCESS, 'Evaluation Submitted');
    } catch (err) {
      handleError(err, { notification: { title: 'Submit Evaluation' } });
    }
  };

  return (
    <Modal className="CompleteEvaluationWizard" show={show} onHide={handleCancel} showClose={false} size="lg">
      <Form
        value={{ ...initFormValues }}
        validator={Types.Validator()}
        onSubmit={step === Types.Step.FIELD_NOTES ? handleNext : handleSubmit}
      >
        <div className="CompleteEvaluationWizard-ProgressHeader">
          <h6 className="CompleteEvaluationWizard-StepLabel fw-semibold">Step {step} of 2</h6>
          <ProgressBar className="CompleteEvaluationWizard-ProgressBar" min={0} max={1} now={step === Types.Step.FIELD_NOTES ? 0.5 : 1} />
        </div>
        {step === Types.Step.FIELD_NOTES && <Step1 />}
        {step === Types.Step.COMPLETION && (
          <Step2
            driverName={userFullName}
            driverId={userEmployeeId}
            startDate={startDate}
            trainerName={trainerFullName}
            type={userRoleName}
          />
        )}
        <div className="CompleteEvaluationWizard-Footer">
          <Form.Button
            type="button"
            variant="outline-primary"
            onClick={step === Types.Step.FIELD_NOTES ? handleCancel : (): void => setStep(Types.Step.FIELD_NOTES)}
          >
            {step === Types.Step.FIELD_NOTES ? 'Cancel' : 'Back'}
          </Form.Button>
          <Form.Button type="submit" variant="primary">
            {step === Types.Step.FIELD_NOTES ? 'Next Step' : 'Submit Evaluation'}
          </Form.Button>
        </div>
      </Form>
    </Modal>
  );
};

export default CompleteEvaluationWizard;
